// src/redux/sagas/makeReservationSaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import { getRestaurantId } from '../../api/tokenUtils';
import type { ReservationRequest, ReservationResponse } from '../../types/tableReservation';
import {
  makeReservationRequest,
  makeReservationSuccess,
  makeReservationFailure,
} from '../slices/makeReservationSlice';
import { fetchTimeSlotsRequest } from '../slices/timeSlotsSlice';

/**
 * Saga to make a table reservation using the new API.
 * Transforms the request and adds restaurant-id header.
 * 
 * @param action - Redux action with reservation request data
 */
function* makeReservationSaga(
  action: PayloadAction<ReservationRequest>
): Generator<unknown, void, AxiosResponse<ReservationResponse>> {
  try {
    const restaurantId = getRestaurantId();
    
    if (!restaurantId) {
      yield put(makeReservationFailure('Restaurant ID not found'));
      return;
    }

    // Call new reservation API with restaurant-id header
    const response = yield call(request<ReservationResponse>, {
      method: 'POST',
      url: ENDPOINTS.TABLE_RESERVATION_API_PATH,
      base: ENDPOINTS.API_V2_BASE_URL,
      data: action.payload,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    // Check if response is successful
    if (response.data.success) {
      yield put(makeReservationSuccess(response.data));
      
      // Refresh time slots for the reserved day
      const date = new Date(action.payload.reservationDate);
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const day = dayNames[date.getDay()];
      yield put(fetchTimeSlotsRequest({ day }));
    } else {
      const errorMsg = response.data.message || response.data.error || 'Failed to make reservation';
      yield put(makeReservationFailure(errorMsg));
    }
  } catch (error: unknown) {
    let errorMessage = 'Failed to make reservation';

    if (axios.isAxiosError(error) && error.response?.data) {
      // Extract backend custom error message from 400/422/500 HTTP responses
      const responseData = error.response.data as Partial<ReservationResponse>;
      errorMessage =
        responseData.message ||
        responseData.error ||
        error.response.data.error ||
        error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    console.log("=== Extracted Error Message ===", errorMessage);
    yield put(makeReservationFailure(errorMessage));
  }
}

export function* watchMakeReservationSaga() {
  yield takeEvery(makeReservationRequest.type, makeReservationSaga);
}